// src/pages/TerritoriesPage.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import PushPinIcon from "@mui/icons-material/PushPin";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { useTheme } from "@mui/material/styles";
import { FileUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import useAuth from "../hooks/useAuth";
import ConfirmDialog from "../components/ConfirmDialog";
import TerritoryQuickCreateModal from "../components/TerritoryQuickCreateModal";

const ID_TONES = [
  { light: "#E7F2FF", dark: "rgba(59,130,246,.16)", color: "#1683EA" },
  { light: "#F0E7FF", dark: "rgba(139,92,246,.16)", color: "#8B5CF6" },
  { light: "#FFF1D7", dark: "rgba(245,158,11,.16)", color: "#E59A00" },
  { light: "#DDF8E9", dark: "rgba(34,197,94,.16)", color: "#16A05A" },
  { light: "#FFE3EA", dark: "rgba(244,63,94,.16)", color: "#EF4565" },
  { light: "#E8E3FF", dark: "rgba(124,58,237,.16)", color: "#7655E8" },
  { light: "#DDF7FB", dark: "rgba(6,182,212,.16)", color: "#13A7C2" },
  { light: "#FFE8D8", dark: "rgba(249,115,22,.16)", color: "#EA741B" },
];

const getTone = (id) => ID_TONES[Math.abs(Number(id) || 0) % ID_TONES.length];

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join("");
};

export default function TerritoriesPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { can, isReadOnly } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [territories, setTerritories] = useState([]);
  const [users, setUsers] = useState([]);
  const [assignedReps, setAssignedReps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    manager: "",
    assigned_reps: [],
  });
  const [open, setOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [openQuickCreate, setOpenQuickCreate] = useState(false);
  const [loading, setLoading] = useState(true);

  // V2 presentation state only
  const [search, setSearch] = useState("");
  const [managerFilter, setManagerFilter] = useState("all");
  const [actionAnchor, setActionAnchor] = useState(null);
  const [actionTerritory, setActionTerritory] = useState(null);

  const isDark = theme.palette.mode === "dark";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [territoryRes, userRes] = await Promise.all([
        API.get("/territories/"),
        API.get("/users/"),
      ]);
      setTerritories(territoryRes.data);
      setUsers(userRes.data);
      console.log("✅ Territories API response:", territoryRes.data);
    } catch (err) {
      console.error("Error fetching data:", err);
      toast.error("Failed to load territories or users");
    } finally {
      setLoading(false);
    }
  };

  // Existing territory navigation behavior, now called explicitly from the action menu.
  const navigateToTerritory = (territory, view = "all") => {
  if (!territory?.id) return;

  navigate(
    `/territory/add/lead?territory_id=${territory.id}&view=${view}`
  );
};

  const handleClose = () => {
    setOpen(false);
    setSelected(null);
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async () => {
    if (!selected) return;
    try {
      const payload = {
        name: formData.name,
        manager_id: formData.manager || null,
        assigned_reps_ids: formData.assigned_reps || [],
      };

      await API.put(`/territories/${selected.id}/`, payload);
      toast.success("Territory updated");
      await fetchData();
      setOpen(false);
    } catch (err) {
      console.error("Error updating territory:", err);
      toast.error("Failed to update territory");
    }
  };

  const handleDelete = () => {
    if (!selected) return;
    setConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await API.delete(`/territories/${selected.id}/`);
      toast.success("🗑️ Territory deleted");
      await fetchData();
      setOpen(false);
    } catch (err) {
      console.error("Error deleting territory:", err);
      toast.error("Failed to delete territory");
    } finally {
      setConfirmDelete(false);
    }
  };

  const handleAddLeads = () => {
    if (!selected?.id) return toast.warn("⚠️ Select a territory first.");
    setOpen(false);
    navigate(`/territory/add/lead?territory_id=${selected.id}`);
  };

  const openActionMenu = (event, territory) => {
    event.stopPropagation();
    setActionAnchor(event.currentTarget);
    setActionTerritory(territory);
  };

  const closeActionMenu = () => {
    setActionAnchor(null);
    setActionTerritory(null);
  };

  const handleMenuEdit = () => {
  const territory = actionTerritory;
  closeActionMenu();
  navigateToTerritory(territory, "edit");
};

const handleMenuUpload = () => {
  const territory = actionTerritory;
  closeActionMenu();
  navigateToTerritory(territory, "upload");
};

  const managerOptions = useMemo(() => {
    return [...new Set(
      territories
        .map((territory) => territory.manager_name)
        .filter(Boolean)
    )].sort((a, b) => a.localeCompare(b));
  }, [territories]);

  const visibleTerritories = useMemo(() => {
    const query = search.trim().toLowerCase();

    return territories.filter((territory) => {
      const matchesSearch =
        !query ||
        String(territory.id ?? "").toLowerCase().includes(query) ||
        String(territory.name ?? "").toLowerCase().includes(query) ||
        String(territory.manager_name ?? "").toLowerCase().includes(query);

      const matchesManager =
        managerFilter === "all" ||
        territory.manager_name === managerFilter;

      return matchesSearch && matchesManager;
    });
  }, [territories, search, managerFilter]);

  const totalLeads = useMemo(
    () =>
      territories.reduce(
        (sum, territory) =>
          sum + Number(territory.leads_count ?? territory.lead_count ?? 0),
        0
      ),
    [territories]
  );

  const managerCount = useMemo(
    () =>
      new Set(
        territories.map((territory) => territory.manager_name).filter(Boolean)
      ).size,
    [territories]
  );

  const headerCardSx = {
    p: { xs: 3, md: 3.25 },
    mb: 2,
    borderRadius: 3.5,
    border: "1px solid",
    borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.06)",
    background: isDark
      ? "linear-gradient(135deg, rgba(30,41,59,.94), rgba(15,23,42,.94))"
      : "linear-gradient(135deg, rgba(255,255,255,1) 0%, rgba(246,250,255,1) 100%)",
    boxShadow: isDark
      ? "0 14px 34px rgba(0,0,0,.28)"
      : "0 14px 36px rgba(15,23,42,.07)",
    overflow: "hidden",
    position: "relative",
    "&::after": {
      content: '""',
      display: { xs: "none", md: "block" },
      position: "absolute",
      width: 380,
      height: 380,
      right: -90,
      top: -205,
      borderRadius: "50%",
      border: "1px solid",
      borderColor: isDark ? "rgba(96,165,250,.08)" : "rgba(37,99,235,.07)",
      boxShadow: isDark
        ? "0 0 0 42px rgba(96,165,250,.025), 0 0 0 86px rgba(96,165,250,.018)"
        : "0 0 0 42px rgba(37,99,235,.025), 0 0 0 86px rgba(37,99,235,.018)",
      pointerEvents: "none",
    },
  };

  const mobileTerritoryCardSx = {
    p: 1.5,
    borderRadius: 3,
    bgcolor: isDark ? "rgba(30,41,59,0.88)" : "background.paper",
    border: "1px solid",
    borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)",
    boxShadow: isDark
      ? "0 8px 24px rgba(0,0,0,0.28)"
      : "0 8px 24px rgba(15,23,42,0.06)",
  };

  return (
    <Box sx={{ p: 3, px: { xs: 1, sm: 3 } }}>
      <Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
        {/* V2 Header Card — global/top navigation is outside this page and untouched */}
        <Card sx={headerCardSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ sm: "center" }}
            justifyContent="space-between"
            sx={{ position: "relative", zIndex: 1 }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box
                sx={{
                  width: 58,
                  height: 58,
                  flex: "0 0 auto",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isDark
                    ? "rgba(59,130,246,.14)"
                    : "rgba(37,99,235,.08)",
                }}
              >
                <PushPinIcon color="primary" />
              </Box>

              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{ lineHeight: 1.25 }}
                >
                  Territories
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Create, manage your territories, upload and assign leads. (
                  {territories.length}{" "}
                  {territories.length === 1 ? "territory" : "territories"})
                </Typography>
              </Box>
            </Stack>

            <Button
              data-tour="add-territory-button"
              variant="contained"
              onClick={() => setOpenQuickCreate(true)}
              sx={{
                borderRadius: "12px",
                fontWeight: 750,
                fontSize: "0.875rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.4,
                px: 2.25,
                py: 1.1,
                whiteSpace: "nowrap",
                width: { xs: "100%", sm: "auto" },
                minWidth: { sm: 150 },
                transition: "all 0.18s ease",
                background: (t) =>
                  `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                color: "#fff",
                boxShadow: (t) =>
                  `0 10px 22px ${t.palette.primary.main}35`,
                "&:hover": {
                  background: (t) =>
                    `linear-gradient(135deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.dark} 100%)`,
                  boxShadow: (t) =>
                    `0 14px 28px ${t.palette.primary.main}4A`,
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              + Add Territory
            </Button>
          </Stack>

          {/* Desktop-only summary strip from the V2 mock */}
          <Stack
            direction="row"
            spacing={1.25}
            sx={{
              mt: 2.5,
              display: { xs: "none", sm: "flex" },
              position: "relative",
              zIndex: 1,
            }}
          >
          {[
  {
    value: territories.length,
    label: "Territories",
    tint: "#F3F8FF",
    darkTint: "rgba(59,130,246,0.08)",
    accent: "#2583E3",
  },
  {
    value: totalLeads.toLocaleString(),
    label: "Total Leads",
    tint: "#F7F5FF",
    darkTint: "rgba(139,92,246,0.08)",
    accent: "#8067D9",
  },
  {
    value: managerCount,
    label: "Managers",
    tint: "#F2FAF6",
    darkTint: "rgba(34,197,94,0.07)",
    accent: "#39A66A",
  },
].map((stat) => (
  <Box
    key={stat.label}
    sx={{
      minWidth: 190,
      px: 2,
      py: 1.5,
      borderRadius: 2.5,

      bgcolor: isDark
        ? stat.darkTint
        : stat.tint,

      border: "1px solid",
      borderColor: isDark
        ? "rgba(255,255,255,0.07)"
        : `${stat.accent}18`,

      position: "relative",
      overflow: "hidden",

      "&::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 12,
        bottom: 12,
        width: 3,
        borderRadius: "0 4px 4px 0",
        bgcolor: stat.accent,
        opacity: 0.75,
      },
    }}
  >
    <Typography
      variant="h6"
      fontWeight={700}
      sx={{ lineHeight: 1.2 }}
    >
      {stat.value}
    </Typography>

    <Typography
      variant="caption"
      color="text.secondary"
    >
      {stat.label}
    </Typography>
  </Box>
))}
          </Stack>
        </Card>

        <TerritoryQuickCreateModal
          open={openQuickCreate}
          onClose={() => setOpenQuickCreate(false)}
          onCreated={fetchData}
          isReadOnly={isReadOnly}
        />

        {/* V2 Territories List */}
        <Card
          sx={{
            borderRadius: 3.5,
            border: "1px solid",
            borderColor: isDark
              ? "rgba(255,255,255,0.08)"
              : "rgba(15,23,42,0.06)",
            boxShadow: isDark
              ? "0 14px 34px rgba(0,0,0,.24)"
              : "0 14px 36px rgba(15,23,42,.065)",
            overflow: "visible",
          }}
        >
          {loading ? (
            <Box sx={{ p: 2 }}>
              {isMobile ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.25,
                    px: 0.5,
                    py: 1,
                  }}
                >
                  {[1, 2, 3].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={92}
                      sx={{ borderRadius: 3 }}
                    />
                  ))}
                </Box>
              ) : (
                <Box sx={{ px: 2, py: 1 }}>
                  <Skeleton
                    variant="rectangular"
                    height={48}
                    sx={{ mb: 2, borderRadius: 2 }}
                  />
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      height={58}
                      sx={{ mb: 0.75, borderRadius: 1.5 }}
                    />
                  ))}
                </Box>
              )}
            </Box>
          ) : territories.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography fontWeight={700} gutterBottom>
                No territories yet.
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                Create a territory to assign leads and reps.
              </Typography>
              {!isReadOnly && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigate("/territory/add")}
                >
                  Create a territory
                </Button>
              )}
            </Box>
          ) : (
            <>
              {/* Search/filter toolbar */}
              <Box
                sx={{
                  px: { xs: 1.5, sm: 2 },
                  py: 1.6,
                  display: "flex",
                  gap: 1.25,
                  flexDirection: { xs: "column", sm: "row" },
                  alignItems: { xs: "stretch", sm: "center" },
                  justifyContent: "space-between",
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.1}
                  sx={{ flex: 1 }}
                >
                  <TextField
                    size="small"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search territories..."
                    inputProps={{ "aria-label": "Search territories" }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRoundedIcon
                            sx={{ fontSize: 21, color: "text.secondary" }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: { xs: "100%", sm: 360 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: isDark
                          ? "rgba(255,255,255,.025)"
                          : "rgba(248,250,252,.72)",
                      },
                    }}
                  />

                  <FormControl
                    size="small"
                    sx={{
                      width: { xs: "100%", sm: 190 },
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2.25,
                        bgcolor: isDark
                          ? "rgba(255,255,255,.025)"
                          : "rgba(248,250,252,.72)",
                      },
                    }}
                  >
                  <Select
  value={managerFilter}
  onChange={(e) => setManagerFilter(e.target.value)}
  inputProps={{ "aria-label": "Filter by manager" }}
  MenuProps={{
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(15,23,42,0.06)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.28)"
            : "0 8px 24px rgba(15,23,42,0.08)",
        p: 0.5,

        "& .MuiMenuItem-root": {
          fontSize: "0.8125rem",
          minHeight: 36,
          borderRadius: 1.5,
          mx: 0,
          px: 1.25,
        },
      },
    },
  }}
>
  <MenuItem value="all">All Managers</MenuItem>

  {managerOptions.map((manager) => (
    <MenuItem key={manager} value={manager}>
      {manager}
    </MenuItem>
  ))}
</Select>
                  </FormControl>
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap", px: { sm: 1 } }}
                >
                  Showing {visibleTerritories.length}{" "}
                  {visibleTerritories.length === 1 ? "territory" : "territories"}
                </Typography>
              </Box>

              <Box sx={{ width: "100%", overflow: "auto" }}>
                {isMobile ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.15,
                      px: 1.25,
                      py: 1.5,
                    }}
                  >
                    {visibleTerritories.map((territory) => {
                      const tone = getTone(territory.id);
                      const managerName = territory.manager_name || "Unassigned";

                      return (
                        <Card key={territory.id} sx={mobileTerritoryCardSx}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1.4}
                          >
                            <Box
                              sx={{
                                width: 48,
                                height: 48,
                                flex: "0 0 auto",
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                bgcolor: isDark ? tone.dark : tone.light,
                                color: tone.color,
                                fontWeight: 800,
                              }}
                            >
                              {territory.id}
                            </Box>

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography
                                fontWeight={750}
                                noWrap
                                sx={{
                                  letterSpacing: "-0.015em",
                                  lineHeight: 1.25,
                                }}
                              >
                                {territory.name}
                              </Typography>

                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.8}
                                sx={{ mt: 0.7 }}
                              >
                                <Avatar
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    fontSize: 10,
                                    fontWeight: 800,
                                    bgcolor: isDark
                                      ? "rgba(59,130,246,.18)"
                                      : "#E3F0FF",
                                    color: "primary.main",
                                  }}
                                >
                                  {getInitials(managerName)}
                                </Avatar>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {managerName}
                                </Typography>
                              </Stack>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: "block",
                                  mt: 0.45,
                                  fontWeight: 600,
                                }}
                              >
                                {Number(
                                  territory.leads_count ??
                                    territory.lead_count ??
                                    0
                                ).toLocaleString()}{" "}
                                leads
                              </Typography>
                            </Box>

                            <IconButton
                              aria-label={`Actions for ${territory.name}`}
                              onClick={(e) => openActionMenu(e, territory)}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2.2,
                                border: "1px solid",
                                borderColor: isDark
                                  ? "rgba(255,255,255,.09)"
                                  : "rgba(37,99,235,.12)",
                                bgcolor: isDark
                                  ? "rgba(255,255,255,.03)"
                                  : "rgba(37,99,235,.035)",
                              }}
                            >
                              <MoreHorizRoundedIcon />
                            </IconButton>
                          </Stack>
                        </Card>
                      );
                    })}
                  </Box>
                ) : (
                  <Table
                    sx={{
                      "& .MuiTableCell-root": {
                        borderBottomColor: isDark
                          ? "rgba(255,255,255,.065)"
                          : "rgba(15,23,42,.065)",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: isDark
                            ? "rgba(255,255,255,0.035)"
                            : "rgba(248,250,252,.82)",
                        }}
                      >
                        {["ID", "Territory Name", "Manager", "Leads Count"].map(
                          (label) => (
                            <TableCell
                              key={label}
                              sx={{
                                py: 1.35,
                                fontSize: 11.5,
                                fontWeight: 800,
                                color: "text.secondary",
                                textTransform: "uppercase",
                                letterSpacing: ".025em",
                              }}
                            >
                              {label}
                            </TableCell>
                          )
                        )}
                        <TableCell
                          sx={{
                            py: 1.35,
                            fontSize: 11.5,
                            fontWeight: 800,
                            color: "text.secondary",
                            textTransform: "uppercase",
                            letterSpacing: ".025em",
                            textAlign: "right",
                          }}
                        >
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {visibleTerritories.map((territory) => {
                        const tone = getTone(territory.id);
                        const managerName =
                          territory.manager_name || "Unassigned";

                        return (
                          <TableRow
                            key={territory.id}
                            sx={{
                              // Intentionally no row click, hover fill, or pointer cursor.
                              "&:last-of-type td": { borderBottom: 0 },
                            }}
                          >
                            <TableCell sx={{ py: 1.05, width: 96 }}>
                              <Box
                                sx={{
                                  width: 38,
                                  height: 28,
                                  borderRadius: 999,
                                  display: "grid",
                                  placeItems: "center",
                                  bgcolor: isDark ? tone.dark : tone.light,
                                  color: tone.color,
                                  fontSize: 12,
                                  fontWeight: 850,
                                  lineHeight: 1,
                                }}
                              >
                                {territory.id}
                              </Box>
                            </TableCell>

                            <TableCell sx={{ py: 1.05 }}>
                              <Typography
                                variant="body2"
                                fontWeight={700}
                                noWrap
                                sx={{
                                  color: "text.primary",
                                  letterSpacing: "-0.01em",
                                }}
                              >
                                {territory.name}
                              </Typography>
                            </TableCell>

                            <TableCell sx={{ py: 1.05 }}>
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Avatar
                                  sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: 10.5,
                                    fontWeight: 800,
                                    bgcolor: isDark
                                      ? "rgba(59,130,246,.18)"
                                      : "#E3F0FF",
                                    color: "primary.main",
                                  }}
                                >
                                  {getInitials(managerName)}
                                </Avatar>
                                <Typography variant="body2">
                                  {managerName}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ py: 1.05 }}>
                              <Typography
                                variant="body2"
                                sx={{ fontVariantNumeric: "tabular-nums" }}
                              >
                                {Number(
                                  territory.leads_count ??
                                    territory.lead_count ??
                                    0
                                ).toLocaleString()}
                              </Typography>
                            </TableCell>

                            <TableCell
                              sx={{
                                py: 0.75,
                                textAlign: "right",
                                width: 92,
                              }}
                            >
                              <IconButton
                                aria-label={`Actions for ${territory.name}`}
                                onClick={(e) => openActionMenu(e, territory)}
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: 2,
                                  border: "1px solid",
                                  borderColor: isDark
                                    ? "rgba(255,255,255,.09)"
                                    : "rgba(37,99,235,.12)",
                                  bgcolor: isDark
                                    ? "rgba(255,255,255,.025)"
                                    : "rgba(37,99,235,.03)",
                                  color: "text.secondary",
                                  "&:hover": {
                                    bgcolor: isDark
                                      ? "rgba(59,130,246,.12)"
                                      : "rgba(37,99,235,.075)",
                                    color: "primary.main",
                                  },
                                }}
                              >
                                <MoreHorizRoundedIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}

                {visibleTerritories.length === 0 && (
                  <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
                    <Typography fontWeight={700}>
                      No matching territories
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      Try changing the search or manager filter.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Card>

        {/* V2 row/card action menu */}
        <Menu
  anchorEl={actionAnchor}
  open={Boolean(actionAnchor)}
  onClose={closeActionMenu}
  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
  transformOrigin={{ vertical: "top", horizontal: "right" }}
  slotProps={{
    paper: {
      sx: {
        mt: 0.5,
        minWidth: 175,
        borderRadius: 2,
        border: "1px solid",
        borderColor: (theme) =>
          theme.palette.mode === "dark"
            ? "rgba(255,255,255,0.08)"
            : "rgba(15,23,42,0.06)",
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? "0 8px 24px rgba(0,0,0,0.28)"
            : "0 8px 24px rgba(15,23,42,0.08)",
        p: 0.5,
      },
    },
  }}
>
  <MenuItem
    onClick={handleMenuEdit}
    sx={{
      borderRadius: 1.5,
      gap: 1.25,
      py: 0.9,
      px: 1.25,
      "&:hover": {
        bgcolor: "action.hover",
      },
    }}
  >
    <EditOutlinedIcon
      sx={{
        fontSize: 18,
        color: "text.secondary",
      }}
    />
    <Typography variant="body2">
      Edit Territory
    </Typography>
  </MenuItem>

  <MenuItem
    data-tour="upload-leads-chip"
    onClick={handleMenuUpload}
    sx={{
      borderRadius: 1.5,
      gap: 1.25,
      py: 0.9,
      px: 1.25,
      "&:hover": {
        bgcolor: "action.hover",
      },
    }}
  >
    <FileUp
      size={17}
      strokeWidth={1.8}
    />
    <Typography variant="body2">
      Upload Leads
    </Typography>
  </MenuItem>
</Menu>

        {/* Existing edit modal retained for compatibility; no business logic changed */}
        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: theme.palette.background.paper,
              boxShadow: 24,
              borderRadius: 2,
              p: 5,
              width: 500,
              maxWidth: "90%",
              minHeight: 420,
            }}
          >
            <Typography variant="h6" mb={3}>
              Edit Territory
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Territory Name"
                name="name"
                fullWidth
                value={formData.name || ""}
                onChange={handleChange}
              />

              <FormControl fullWidth>
                <InputLabel>Manager</InputLabel>
                <Select
                  name="manager"
                  label="Manager"
                  value={formData.manager || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Assigned Reps</InputLabel>
                <Select
                  multiple
                  value={assignedReps}
                  label="Assigned Reps"
                  onChange={(e) => setAssignedReps(e.target.value)}
                  renderValue={(selectedIds) =>
                    users
                      .filter((u) => selectedIds.includes(u.id))
                      .map((u) => `${u.first_name} ${u.last_name}`)
                      .join(", ")
                  }
                >
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      <span>
                        {u.first_name} {u.last_name}
                      </span>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack
                direction="row"
                spacing={2}
                justifyContent="space-between"
                mt={4}
              >
                {can.canDelete && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleDelete}
                    sx={{ flex: 1 }}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                  sx={{ flex: 1 }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleAddLeads}
                  sx={{ flex: 1 }}
                >
                  + Add Leads
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Modal>

        <ConfirmDialog
          open={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleConfirmDelete}
          title="Delete Territory"
          message="Are you sure you want to delete this territory? All associated leads will be unassigned. This action cannot be undone."
          itemName={selected?.name ? `Territory: ${selected.name}` : null}
        />
      </Box>
    </Box>
  );
}